import React from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { useMessage } from '@tmapy/intl';
import { Checkbox, Select } from '@tmapy/style-guide';

import './FormatCard.scss';

import type { PublishsetFormat, ServerErrors } from '../../types';
import type { ExportToolResponse } from '../../openapi/types/ExportTool';
import { useFormItemServerErrors } from '../../hooks';

import { publishsetFormMessages } from './publishsetMessages';

export type SelectItem = { value: string; label: string };

type FormatCardProps = {
  format: PublishsetFormat;
  tools: ExportToolResponse[];
  isToolsLoaded: boolean;
  isChecked: boolean;
  selectedTool: ExportToolResponse | null;
  selectedTileindexUrl: string;
  tileindexItems: SelectItem[];
  isTileindexMissing: boolean;
  serverErrors?: ServerErrors;
  getToolLabel(tool: ExportToolResponse): string;
  onToggle(format: PublishsetFormat, isChecked: boolean): void;
  onToolChange(formatUrl: string, toolUrl: string): void;
  onTileindexChange(formatUrl: string, tileindexUrl: string): void;
};

export const FormatCard: React.FC<FormatCardProps> = ({
  format,
  tools,
  isToolsLoaded,
  isChecked,
  selectedTool,
  selectedTileindexUrl,
  tileindexItems,
  isTileindexMissing,
  serverErrors,
  getToolLabel,
  onToggle,
  onToolChange,
  onTileindexChange,
}) => {
  const formatMessage = useMessage();
  const cardServerErrors = useFormItemServerErrors(serverErrors);

  const isUnavailable = isToolsLoaded && tools.length === 0;
  const toolErrorMessage =
    cardServerErrors.export_tool?.errorMessage ??
    cardServerErrors.format?.errorMessage ??
    cardServerErrors.non_field_errors?.errorMessage;
  const tileindexErrorMessage = isTileindexMissing
    ? formatMessage(publishsetFormMessages.tileindexRequired)
    : cardServerErrors.tileindex?.errorMessage;
  const isInvalid = isChecked && !!(toolErrorMessage || tileindexErrorMessage);

  return (
    <div
      className={classNames('dmx-formatCard', {
        'dmx-formatCard-isChecked': isChecked,
        'dmx-formatCard-isUnavailable': isUnavailable,
        'dmx-formatCard-isInvalid': isInvalid,
      })}
    >
      <Checkbox
        isChecked={isChecked}
        isDisabled={isUnavailable && !isChecked}
        data={format}
        onChange={() => onToggle(format, isChecked)}
      >
        <span className='dmx-formatCard--name'>{format.name}</span>
      </Checkbox>
      {isUnavailable && (
        <div className='dmx-formatCard--noTool'>
          <FormattedMessage {...publishsetFormMessages.exportToolNoTool} />
        </div>
      )}
      {!isChecked && tools.length > 0 && (
        <div className='dmx-formatCard--hint'>
          <FormattedMessage
            {...publishsetFormMessages.exportToolsAvailable}
            values={{ count: tools.length }}
          />
        </div>
      )}
      {isChecked && (
        <div className='dmx-formatCard--fields'>
          <div className='dmx-formatCard--field'>
            <span className='dmx-formatCard--fieldLabel'>
              <FormattedMessage {...publishsetFormMessages.exportTool} />
            </span>
            {tools.length === 1 ? (
              <div className='dmx-formatCard--singleTool'>
                <span className='dmx-formatCard--singleToolName'>{getToolLabel(tools[0])}</span>
                <span className='dmx-formatCard--hint'>
                  <FormattedMessage {...publishsetFormMessages.exportToolSingle} />
                </span>
              </div>
            ) : (
              <Select
                name={`export_tool_${format.url}`}
                value={selectedTool?.url ?? ''}
                isInvalid={!!toolErrorMessage}
                items={[
                  { value: '', label: '' },
                  ...tools.map((tool) => ({ value: tool.url, label: getToolLabel(tool) })),
                ]}
                onChange={(e, eventData) => onToolChange(format.url, eventData.value)}
              />
            )}
            {toolErrorMessage && <div className='dmx-formatCard--error'>{toolErrorMessage}</div>}
          </div>
          {selectedTool?.is_package && (
            <div className='dmx-formatCard--field'>
              <span className='dmx-formatCard--fieldLabel dmx-formatCard--fieldLabel-isRequired'>
                <FormattedMessage {...publishsetFormMessages.tileindex} />
              </span>
              <Select
                name={`tileindex_${format.url}`}
                value={selectedTileindexUrl}
                isRequired
                isInvalid={!!tileindexErrorMessage}
                items={[{ value: '', label: '' }, ...tileindexItems]}
                onChange={(e, eventData) => onTileindexChange(format.url, eventData.value)}
              />
              {tileindexErrorMessage && (
                <div className='dmx-formatCard--error'>{tileindexErrorMessage}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
